import { supabase } from '../lib/supabase';
import type { WallDragMode } from '../types';
import {
  APP_UI_FONT_STORAGE_KEY,
  type AppUIFont,
} from './appUIFont';
import { DEFAULT_ICON_GRID_CELLS, parseIconGridCells, type IconGridCells } from './canvasIcon';

const GUEST_KEY = 'netprobuilder-account-ui-prefs';
const userKey = (userId: string) => `${GUEST_KEY}:${userId}`;

const BADGE_ANCHORS = [
  'top-left',
  'top-right',
  'center-top',
  'left',
  'right',
  'bottom-left',
  'bottom-right',
  'center-bottom',
] as const;

export type AccountBadgeAnchor = (typeof BADGE_ANCHORS)[number];
export type AccountBadgeShape = 'square' | 'circle';
export type AccountCurrency = 'USD' | 'ZAR' | 'GBP';
export type AccountAppTheme = 'default' | 'dark';
export type AccountFloorPlanUnits = 'metric' | 'imperial';
export type SnapToGridTarget = 'icons' | 'walls' | 'both';

export interface AccountUiPrefs {
  snapToGrid: boolean;
  /** What snap-to-grid applies to when it is enabled. Default: both. */
  snapToGridTarget: SnapToGridTarget;
  gridSize: number;
  /** Inner-grid squares occupied by each canvas icon (N×N). */
  iconGridCells: IconGridCells;
  showGrid: boolean;
  showLegend: boolean;
  alwaysShowExpandedEquipment: boolean;
  portsDisplayMode: 'none' | 'full';
  canvasBackground: 'light' | 'dark';
  badgeAnchor: AccountBadgeAnchor;
  /** Collapsed equipment badge corners: square (rounded) or circle. */
  badgeShape: AccountBadgeShape;
  panelVisualization: 'shaded' | 'minimalist';
  wallDragMode: WallDragMode;
  showDesignInfoOverlay: boolean;
  showMinimap: boolean;
  showManufacturerInEquipmentList: boolean;
  expandBadgesOnHover: boolean;
  colourizePorts: boolean;
  /** When true, every floating equipment panel opens in Advanced table mode and the header switch is locked on. */
  equipmentPanelAdvancedDefault: boolean;
  /** Hide the port preview block in the floating equipment panel. */
  equipmentPanelPreviewHidden: boolean;
  showAPRange: boolean;
  autosaveEnabled: boolean;
  autosaveIntervalMinutes: number;
  currency: AccountCurrency;
  appTheme: AccountAppTheme;
  appUIFont: AppUIFont;
  showTowerLabels: boolean;
  floorPlanUnits: AccountFloorPlanUnits;
  enableCustomTemplates: boolean;
  showBetaBadges: boolean;
  enableAdvancedCableRun: boolean;
  canvasCollabMode: 'default' | 'commitOnDrop' | 'livePreview';
  /** Last Cable Run routing picked in the toolbar popover. */
  cableRunRouting: 'straight' | 'flexi';
}

export const ACCOUNT_UI_PREF_DEFAULTS: AccountUiPrefs = {
  snapToGrid: true,
  snapToGridTarget: 'both',
  gridSize: 10,
  iconGridCells: DEFAULT_ICON_GRID_CELLS,
  showGrid: true,
  showLegend: true,
  alwaysShowExpandedEquipment: false,
  portsDisplayMode: 'none',
  canvasBackground: 'light',
  badgeAnchor: 'top-left',
  badgeShape: 'square',
  panelVisualization: 'minimalist',
  wallDragMode: 'extend',
  showDesignInfoOverlay: false,
  showMinimap: true,
  showManufacturerInEquipmentList: true,
  expandBadgesOnHover: false,
  colourizePorts: true,
  equipmentPanelAdvancedDefault: false,
  equipmentPanelPreviewHidden: false,
  showAPRange: false,
  autosaveEnabled: true,
  autosaveIntervalMinutes: 2,
  currency: 'ZAR',
  appTheme: 'default',
  appUIFont: 'inter',
  showTowerLabels: true,
  floorPlanUnits: 'metric',
  enableCustomTemplates: false,
  showBetaBadges: true,
  enableAdvancedCableRun: false,
  canvasCollabMode: 'default',
  cableRunRouting: 'straight',
};

type StoredAccountUiPrefs = AccountUiPrefs & { v: 1 };

function isBadgeAnchor(value: unknown): value is AccountBadgeAnchor {
  return typeof value === 'string' && (BADGE_ANCHORS as readonly string[]).includes(value);
}

function parsePartialPrefs(raw: unknown): Partial<AccountUiPrefs> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const partial: Partial<AccountUiPrefs> = {};

  const bool = (key: keyof AccountUiPrefs) => {
    if (key in o && typeof o[key] === 'boolean') {
      (partial as Record<string, unknown>)[key] = o[key];
    }
  };

  bool('snapToGrid');
  bool('showGrid');
  bool('showLegend');
  bool('alwaysShowExpandedEquipment');
  bool('showDesignInfoOverlay');
  bool('showMinimap');
  bool('showManufacturerInEquipmentList');
  bool('expandBadgesOnHover');
  bool('colourizePorts');
  bool('equipmentPanelAdvancedDefault');
  bool('equipmentPanelPreviewHidden');
  bool('showAPRange');
  bool('autosaveEnabled');
  bool('showTowerLabels');
  bool('enableCustomTemplates');
  bool('showBetaBadges');
  bool('enableAdvancedCableRun');

  if (o.snapToGridTarget === 'icons' || o.snapToGridTarget === 'walls' || o.snapToGridTarget === 'both') {
    partial.snapToGridTarget = o.snapToGridTarget;
  } else if (typeof o.snapWallsToGrid === 'boolean') {
    partial.snapToGridTarget = o.snapWallsToGrid ? 'both' : 'icons';
  }

  if (o.canvasCollabMode === 'default' || o.canvasCollabMode === 'commitOnDrop' || o.canvasCollabMode === 'livePreview') {
    partial.canvasCollabMode = o.canvasCollabMode;
  }

  if ('gridSize' in o && typeof o.gridSize === 'number' && Number.isFinite(o.gridSize)) {
    partial.gridSize = Math.max(1, Math.min(200, Math.round(o.gridSize)));
  }
  const iconCells = parseIconGridCells(o.iconGridCells);
  if (iconCells) partial.iconGridCells = iconCells;
  if ('autosaveIntervalMinutes' in o && typeof o.autosaveIntervalMinutes === 'number' && Number.isFinite(o.autosaveIntervalMinutes)) {
    partial.autosaveIntervalMinutes = Math.max(1, Math.min(60, o.autosaveIntervalMinutes));
  }
  if (o.portsDisplayMode === 'full' || o.portsDisplayMode === 'mini') partial.portsDisplayMode = 'full';
  else if (o.portsDisplayMode === 'none') partial.portsDisplayMode = 'none';
  if (o.canvasBackground === 'dark' || o.canvasBackground === 'light') {
    partial.canvasBackground = o.canvasBackground;
  }
  if (isBadgeAnchor(o.badgeAnchor)) partial.badgeAnchor = o.badgeAnchor;
  if (o.badgeShape === 'square' || o.badgeShape === 'circle') partial.badgeShape = o.badgeShape;
  if (o.panelVisualization === 'minimalist' || o.panelVisualization === 'shaded') {
    partial.panelVisualization = o.panelVisualization;
  }
  if (o.wallDragMode === 'extend' || o.wallDragMode === 'default') {
    partial.wallDragMode = o.wallDragMode;
  }
  if (o.currency === 'USD' || o.currency === 'ZAR' || o.currency === 'GBP') {
    partial.currency = o.currency;
  }
  if (o.appTheme === 'dark' || o.appTheme === 'default') partial.appTheme = o.appTheme;
  if (o.appUIFont === 'inter' || o.appUIFont === 'geist' || o.appUIFont === 'plus-jakarta') {
    partial.appUIFont = o.appUIFont;
  }
  if (o.floorPlanUnits === 'metric' || o.floorPlanUnits === 'imperial') {
    partial.floorPlanUnits = o.floorPlanUnits;
  }
  if (o.cableRunRouting === 'straight' || o.cableRunRouting === 'flexi') {
    partial.cableRunRouting = o.cableRunRouting;
  }

  return Object.keys(partial).length > 0 ? partial : null;
}

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readLegacyFragments(): Partial<AccountUiPrefs> {
  const partial: Partial<AccountUiPrefs> = {};
  try {
    const badge = localStorage.getItem('netprobuilder-badge-anchor');
    if (isBadgeAnchor(badge)) partial.badgeAnchor = badge;
  } catch {
    /* ignore */
  }
  try {
    const expanded = localStorage.getItem('netprobuilder-always-show-expanded');
    if (expanded !== null) partial.alwaysShowExpandedEquipment = JSON.parse(expanded);
  } catch {
    /* ignore */
  }
  try {
    const ports = localStorage.getItem('netprobuilder-ports-display-mode');
    if (ports) {
      const parsed = JSON.parse(ports);
      if (parsed === 'mini' || parsed === 'full') partial.portsDisplayMode = 'full';
      else if (parsed === 'none') partial.portsDisplayMode = 'none';
    }
  } catch {
    /* ignore */
  }
  try {
    const bg = localStorage.getItem('netprobuilder-canvas-background');
    if (bg === 'dark' || bg === 'light') partial.canvasBackground = bg;
  } catch {
    /* ignore */
  }
  try {
    const panel = localStorage.getItem('netprobuilder-panel-visualization');
    if (panel === 'minimalist' || panel === 'shaded') partial.panelVisualization = panel;
  } catch {
    /* ignore */
  }
  try {
    const colourize = localStorage.getItem('netprobuilder-colourize-ports');
    if (colourize !== null) partial.colourizePorts = JSON.parse(colourize);
  } catch {
    /* ignore */
  }
  try {
    const currency = localStorage.getItem('netprobuilder-currency');
    if (currency === 'USD' || currency === 'ZAR' || currency === 'GBP') partial.currency = currency;
  } catch {
    /* ignore */
  }
  try {
    const theme = localStorage.getItem('netprobuilder-app-theme');
    if (theme === 'dark' || theme === 'default') partial.appTheme = theme;
  } catch {
    /* ignore */
  }
  try {
    const font = localStorage.getItem(APP_UI_FONT_STORAGE_KEY);
    if (font === 'inter' || font === 'geist' || font === 'plus-jakarta') partial.appUIFont = font;
  } catch {
    /* ignore */
  }
  try {
    const autosave = readJson('netprobuilder-autosave-settings') as { enabled?: boolean; intervalMinutes?: number } | null;
    if (autosave && typeof autosave.enabled === 'boolean') partial.autosaveEnabled = autosave.enabled;
    if (autosave && typeof autosave.intervalMinutes === 'number') {
      partial.autosaveIntervalMinutes = autosave.intervalMinutes;
    }
  } catch {
    /* ignore */
  }
  try {
    const flags = readJson('netprobuilder-feature-flags') as Partial<AccountUiPrefs> | null;
    if (flags) {
      if (typeof flags.enableCustomTemplates === 'boolean') partial.enableCustomTemplates = flags.enableCustomTemplates;
      if (typeof flags.showBetaBadges === 'boolean') partial.showBetaBadges = flags.showBetaBadges;
      if (typeof flags.enableAdvancedCableRun === 'boolean') partial.enableAdvancedCableRun = flags.enableAdvancedCableRun;
    }
  } catch {
    /* ignore */
  }
  try {
    const site = readJson('netprobuilder_site_settings') as { showTowerLabels?: boolean } | null;
    if (site && typeof site.showTowerLabels === 'boolean') partial.showTowerLabels = site.showTowerLabels;
  } catch {
    /* ignore */
  }
  try {
    const userPrefs = readJson('netprobuilder-user-preferences') as { lastUsedMeasurementSystem?: string } | null;
    if (userPrefs?.lastUsedMeasurementSystem === 'imperial' || userPrefs?.lastUsedMeasurementSystem === 'metric') {
      partial.floorPlanUnits = userPrefs.lastUsedMeasurementSystem;
    }
  } catch {
    /* ignore */
  }
  return partial;
}

function writeLegacyMirrors(prefs: AccountUiPrefs): void {
  try {
    localStorage.setItem('netprobuilder-badge-anchor', prefs.badgeAnchor);
    localStorage.setItem('netprobuilder-always-show-expanded', JSON.stringify(prefs.alwaysShowExpandedEquipment));
    localStorage.setItem('netprobuilder-ports-display-mode', JSON.stringify(prefs.portsDisplayMode));
    localStorage.setItem('netprobuilder-canvas-background', prefs.canvasBackground);
    localStorage.setItem('netprobuilder-panel-visualization', prefs.panelVisualization);
    localStorage.setItem('netprobuilder-colourize-ports', JSON.stringify(prefs.colourizePorts));
    localStorage.setItem('netprobuilder-currency', prefs.currency);
    localStorage.setItem('netprobuilder-app-theme', prefs.appTheme);
    localStorage.setItem(APP_UI_FONT_STORAGE_KEY, prefs.appUIFont);
    localStorage.setItem(
      'netprobuilder-autosave-settings',
      JSON.stringify({ enabled: prefs.autosaveEnabled, intervalMinutes: prefs.autosaveIntervalMinutes })
    );
    localStorage.setItem(
      'netprobuilder-feature-flags',
      JSON.stringify({
        enableCustomTemplates: prefs.enableCustomTemplates,
        showBetaBadges: prefs.showBetaBadges,
        enableAdvancedCableRun: prefs.enableAdvancedCableRun,
      })
    );
    localStorage.setItem(
      'netprobuilder_site_settings',
      JSON.stringify({ showTowerLabels: prefs.showTowerLabels })
    );
    const existing = (readJson('netprobuilder-user-preferences') as Record<string, unknown> | null) ?? {};
    localStorage.setItem(
      'netprobuilder-user-preferences',
      JSON.stringify({
        lastUsedPrimaryUnit: 'inches',
        ...existing,
        lastUsedMeasurementSystem: prefs.floorPlanUnits,
      })
    );
  } catch (error) {
    console.warn('Failed to mirror designer UI preferences:', error);
  }
}

function storageKey(userId: string | null | undefined): string {
  return userId ? userKey(userId) : GUEST_KEY;
}

function readBlobPartial(userId: string | null | undefined): Partial<AccountUiPrefs> | null {
  if (userId) {
    const fromUser = parsePartialPrefs(readJson(userKey(userId)));
    if (fromUser) return fromUser;
  }
  return parsePartialPrefs(readJson(GUEST_KEY));
}

export function mergeAccountUiPrefs(
  ...parts: Array<Partial<AccountUiPrefs> | null | undefined>
): AccountUiPrefs {
  return Object.assign({}, ACCOUNT_UI_PREF_DEFAULTS, ...parts.filter(Boolean));
}

/** Saved blob only — null if this account has never persisted designer prefs. */
export function readSavedAccountUiPrefs(userId: string | null | undefined): AccountUiPrefs | null {
  const partial = readBlobPartial(userId);
  if (!partial) return null;
  return mergeAccountUiPrefs(readLegacyFragments(), partial);
}

export function resolveAccountUiPrefs(userId: string | null | undefined): AccountUiPrefs {
  return mergeAccountUiPrefs(readLegacyFragments(), readBlobPartial(userId));
}

export function writeAccountUiPrefs(
  prefs: AccountUiPrefs,
  userId: string | null | undefined
): void {
  const payload: StoredAccountUiPrefs = { v: 1, ...prefs };
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(payload));
    if (userId) {
      localStorage.setItem(GUEST_KEY, JSON.stringify(payload));
    }
    writeLegacyMirrors(prefs);
  } catch (error) {
    console.error('Failed to save designer UI preferences:', error);
  }
}

export function accountUiPrefsFromUnknown(raw: unknown): Partial<AccountUiPrefs> | null {
  return parsePartialPrefs(raw);
}

let cloudSaveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingCloudPrefs: AccountUiPrefs | null = null;
let pendingCloudUserId: string | null = null;

async function pushAccountUiPrefsToCloud(prefs: AccountUiPrefs, userId: string): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ ui_preferences: { v: 1, ...prefs } })
    .eq('id', userId);

  if (error) {
    console.warn('Failed to save designer UI preferences to account:', error.message);
  }
}

export function scheduleCloudSaveAccountUiPrefs(
  prefs: AccountUiPrefs,
  userId: string | null | undefined
): void {
  if (!userId) return;
  pendingCloudPrefs = prefs;
  pendingCloudUserId = userId;
  if (cloudSaveTimer) clearTimeout(cloudSaveTimer);
  cloudSaveTimer = setTimeout(() => {
    const toSave = pendingCloudPrefs;
    const id = pendingCloudUserId;
    cloudSaveTimer = null;
    pendingCloudPrefs = null;
    pendingCloudUserId = null;
    if (toSave && id) {
      void pushAccountUiPrefsToCloud(toSave, id);
    }
  }, 400);
}

/**
 * Load this account's designer prefs from the cloud.
 * Cloud wins when present; otherwise local prefs are uploaded.
 * Returns prefs that should be applied to the live store, or null if nothing new.
 */
export async function hydrateAccountUiPrefsFromCloud(
  userId: string
): Promise<AccountUiPrefs | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('ui_preferences')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.warn('Failed to load designer UI preferences from account:', error.message);
    return readSavedAccountUiPrefs(userId);
  }

  const cloud = parsePartialPrefs(data?.ui_preferences);
  if (cloud) {
    const merged = mergeAccountUiPrefs(readLegacyFragments(), readBlobPartial(userId), cloud);
    writeAccountUiPrefs(merged, userId);
    return merged;
  }

  const local = readSavedAccountUiPrefs(userId);
  if (local) {
    void pushAccountUiPrefsToCloud(local, userId);
  }
  return local;
}

export function persistAccountUiPrefs(
  prefs: AccountUiPrefs,
  userId: string | null | undefined
): void {
  writeAccountUiPrefs(prefs, userId);
  scheduleCloudSaveAccountUiPrefs(prefs, userId);
}

export function patchAndPersistAccountUiPrefs(
  partial: Partial<AccountUiPrefs>,
  userId: string | null | undefined
): void {
  persistAccountUiPrefs(mergeAccountUiPrefs(readLegacyFragments(), readBlobPartial(userId), partial), userId);
}

export function settingsForNewCollection(prefs: AccountUiPrefs) {
  return {
    ...prefs,
    canvasPanX: 0,
    canvasPanY: 0,
    canvasZoom: 1,
    showCanvasToolbar: true,
  };
}
